import React from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import './FmTabs.css';

export interface FmTabItem {
  value: string;
  label: React.ReactNode;
  /** Panel content. Panels of the same element type belong in an FmSelector lens instead. */
  content: React.ReactNode;
  disabled?: boolean;
  'data-testid'?: string;
}

export interface FmTabsProps {
  items: FmTabItem[];
  /** Accessible name of the tab list. */
  label: string;
  /** Active tab (controlled). */
  value?: string;
  /** Initially active tab (uncontrolled); defaults to the first tab. */
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  'data-testid'?: string;
}

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

/**
 * FmTabs — panels that differ in element type, on Radix Tabs, which owns the
 * tablist/tab/tabpanel wiring, roving focus and arrow-key activation.
 */
export const FmTabs: React.FC<FmTabsProps> = ({
  items,
  label,
  value,
  defaultValue,
  onValueChange,
  className,
  'data-testid': dataTestId,
}) => (
  <Tabs.Root
    className={cx('fm-tabs', className)}
    value={value}
    defaultValue={value === undefined ? (defaultValue ?? items[0]?.value) : undefined}
    onValueChange={onValueChange}
    data-testid={dataTestId}
  >
    <Tabs.List className="fm-tabs__list" aria-label={label}>
      {items.map((item) => (
        <Tabs.Trigger
          key={item.value}
          value={item.value}
          disabled={item.disabled}
          className="fm-tabs__tab"
          data-testid={item['data-testid']}
        >
          {item.label}
        </Tabs.Trigger>
      ))}
    </Tabs.List>
    {items.map((item) => (
      <Tabs.Content key={item.value} value={item.value} className="fm-tabs__panel">
        {item.content}
      </Tabs.Content>
    ))}
  </Tabs.Root>
);

FmTabs.displayName = 'FmTabs';

/*
 * Compound parts for an archetype-owned tab set whose list and panel are not
 * siblings: the operational workspace's stage rail over its stage body, the
 * capability showcase's vertical list with each panel under its own trigger.
 * Radix still owns the roles, ids and roving focus; the archetype only places
 * the parts.
 */
export const FmTabsRoot = React.forwardRef<HTMLDivElement, Tabs.TabsProps>((props, ref) => (
  <Tabs.Root ref={ref} {...props} />
));
FmTabsRoot.displayName = 'FmTabsRoot';

export const FmTabsList = React.forwardRef<HTMLDivElement, Tabs.TabsListProps>((props, ref) => (
  <Tabs.List ref={ref} {...props} />
));
FmTabsList.displayName = 'FmTabsList';

export const FmTabsTrigger = React.forwardRef<HTMLButtonElement, Tabs.TabsTriggerProps>(
  (props, ref) => <Tabs.Trigger ref={ref} {...props} />,
);
FmTabsTrigger.displayName = 'FmTabsTrigger';

export const FmTabsPanel = React.forwardRef<HTMLDivElement, Tabs.TabsContentProps>((props, ref) => (
  <Tabs.Content ref={ref} {...props} />
));
FmTabsPanel.displayName = 'FmTabsPanel';
